import { memo, useRef, useState, useEffect, useMemo } from "react";
import { Terminal, Mail, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

interface Update {
  type: string;
  status: string;
  step: string;
  message: string;
  details?: {
    provider?: string;
    status?: string;
  };
  progress?: number;
  timestamp?: number;
  account?: string;
}

interface TerminalLogProps {
  updates: Update[];
  testSteps: Array<{ id: string; label: string; icon: string }>;
}

const renderActivityIcon = (step: string) => {
  switch (step) {
    case "sending_email":
      return <Mail className="h-3.5 w-3.5 text-blue-400" />;
    case "mx_check":
      return <AlertCircle className="h-3.5 w-3.5 text-yellow-400" />;
    case "blacklist_check":
      return <AlertCircle className="h-3.5 w-3.5 text-orange-400" />;
    case "email_received":
      return <CheckCircle className="h-3.5 w-3.5 text-green-400" />;
    case "test_completed":
      return <CheckCircle className="h-3.5 w-3.5 text-green-400" />;
    default:
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />;
  }
};

export const TerminalLog = memo(({ updates: rawUpdates, testSteps }: TerminalLogProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const lastScrollHeight = useRef(0);

  // Memoize the updates and grouped updates
  const updates = useMemo(() => rawUpdates, [rawUpdates]);
  
  // Memoize the grouped updates calculation
  const groupedUpdates = useMemo(() => {
    return updates.reduce((acc, update) => {
      if (!acc[update.step]) {
        acc[update.step] = [];
      }
      acc[update.step].push(update);
      return acc;
    }, {} as Record<string, Update[]>);
  }, [updates]);

  // Handle scroll events
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(isAtBottom);
    }
  };

  // Scroll to bottom for new updates if auto-scroll is enabled
  useEffect(() => {
    if (scrollRef.current && autoScroll) {
      const currentScrollHeight = scrollRef.current.scrollHeight;
      if (currentScrollHeight > lastScrollHeight.current) {
        scrollRef.current.scrollTop = currentScrollHeight;
      }
      lastScrollHeight.current = currentScrollHeight;
    }
  }, [updates, autoScroll]);

  return (
    <div className="bg-zinc-950 rounded-lg border border-zinc-800">
      {/* Terminal Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-zinc-400" />
          <span className="text-sm font-medium text-zinc-300">Activity Log</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-full bg-red-500/80" />
          <div className="h-3 w-3 rounded-full bg-yellow-500/80" />
          <div className="h-3 w-3 rounded-full bg-green-500/80" />
        </div>
      </div>
      
      {/* Terminal Content */}
      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-[500px] overflow-y-auto"
      >
        <div className="p-4">
          {Object.entries(groupedUpdates).map(([step, stepUpdates]) => (
            <div key={step} className="mb-6 last:mb-0">
              {/* Step Header */}
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-zinc-300 border-b border-zinc-800 pb-2">
                <span className="mt-0.5 shrink-0">
                  {testSteps.find(s => s.id === step)?.icon || renderActivityIcon(step)}
                </span>
                <span className="capitalize">{step.replace(/_/g, ' ')}</span>
              </div>

              {/* Step Updates */}
              <div className="space-y-2 pl-6">
                {stepUpdates.map((update) => {
                  if (step === "sending_test" && !update.account) return null;

                  return (
                    <div
                      key={`${update.step}-${update.message}-${update.details?.provider || ""}-${update.timestamp}`}
                      className="flex items-start gap-2 text-sm font-mono"
                    >
                      {/* Timestamp */}
                      <span className="text-zinc-500 min-w-[65px] text-xs select-none">
                        {update.timestamp ? new Date(update.timestamp).toLocaleTimeString("en-US", {
                          hour12: false,
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        }) : ""}
                      </span>
                      
                      {/* Message with Status Indicator */}
                      <div className="flex-1 flex items-start gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                          update.status === "completed" ? "bg-green-500" :
                          update.status === "failed" ? "bg-red-500" :
                          "bg-yellow-500"
                        }`} />
                        
                        <span className={`flex-1 break-all ${
                          update.status === "completed" ? "text-green-300" :
                          update.status === "failed" ? "text-red-300" :
                          "text-zinc-300"
                        }`}>
                          {step === "sending_test" && update.account ? (
                            <span>📧 Sent to: <span className="text-blue-300">{update.account}</span></span>
                          ) : update.message}
                          
                          {update.details?.provider && (
                            <span className="ml-2 text-[10px] py-0.5 px-1.5 bg-zinc-800/50 text-zinc-400 rounded border border-zinc-700/50 inline-flex items-center">
                              {update.details.provider}
                            </span>
                          )}
                          
                          {update.details?.status && (
                            <span className={`ml-2 text-[10px] py-0.5 px-1.5 rounded border inline-flex items-center ${
                              update.details.status === "Inbox" ? "bg-green-900/30 text-green-300 border-green-700/30" :
                              "bg-red-900/30 text-red-300 border-red-700/30"
                            }`}>
                              {update.details.status}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}); 